import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import DashboardLayout from "src/components/layout/dashboard/Layout";
import PageHeader from "src/components/common/PageHeader";
import Card from "src/components/common/card/Card";
import Input from "src/components/ui/Input";
import Textarea from "src/components/ui/Textarea";
import Dropdown from "src/components/ui/Dropdown";
import TableV2 from "src/components/ui/TableV2";
import Button from "src/components/ui/Button";

import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import * as purchaseActions from "src/redux/slices/purchaseSlice";
import { toast } from "react-toastify";
import { currencyFormatter } from "src/utils/common";
import productApis from "src/api/product";
import supplierApis from "src/api/supplier";
import purchaseOrderApis from "src/api/purchaseOrder";
import { PurchaseOrderProduct } from "src/types/purchase.types";
import { Product } from "src/types/product.types";
import { Supplier } from "src/types/supplier.types";
import { Trash } from "lucide-react";

const TAX_PERCENT = 18; // GST 18%

const PurchaseForm: React.FC = () => {
  const { purchaseId } = useParams();
  const navigate = useNavigate();

  const dispatch = useAppDispatch();
  const {
    user: { data: user },
  } = useAppSelector((state) => state.authTenant);
  const { create, edit } = useAppSelector((s) => s.purchase);

  // ------------------ Supplier ------------------
  const [supplierId, setSupplierId] = useState<string>("");
  const [supplier, setSupplier] = useState<Supplier | null>(null);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);

  // ------------------ Products ------------------
  const [products, setProducts] = useState<Product[]>([]);
  const [cart, setCart] = useState<PurchaseOrderProduct[]>([]);

  // ------------------ Summary ------------------
  const [discountPercentage, setDiscountPercentage] = useState<string>("");
  const [paymentStatus, setPaymentStatus] = useState<"paid">("paid");
  const [remarks, setRemarks] = useState("");
  const [orderStatus, setOrderStatus] = useState<
    "draft" | "completed" | "cancelled"
  >("completed");

  const [isLoadingDetails, setIsLoadingDetails] = useState(false);

  // ------------------ Load Data ------------------
  useEffect(() => {
    const loadDropdowns = async () => {
      try {
        const [supRes, prodRes] = await Promise.all([
          supplierApis.getSupplierList({
            page: 1,
            limit: 1000,
            status: "active",
          }),
          productApis.getProductList({
            page: 1,
            limit: 1000,
          }),
        ]);

        const supplierList = supRes?.data?.data || supRes?.data || [];
        const productList = prodRes?.data?.data || prodRes?.data || [];

        setSuppliers(supplierList);
        setProducts(productList);
      } catch (err) {
        console.error("Dropdown load failed:", err);
        toast.error("Failed to load suppliers/products");
      }
    };

    loadDropdowns();
  }, []);

  useEffect(() => {
    const loadPurchaseDetails = async () => {
      if (!purchaseId) return;
      try {
        setIsLoadingDetails(true);
        const res = await purchaseOrderApis.getDetails(purchaseId);
        const order = res?.data;

        if (!order) {
          toast.error("Purchase order not found");
          navigate("/app/purchase/list");
          return;
        }

        setSupplierId(order?.supplierId?._id || "");
        setSupplier(order?.supplierId || null);
        const loadedStatus = order?.orderStatus || "completed";
        setOrderStatus(loadedStatus);
        setPaymentStatus("paid");
        setRemarks(order?.remarks || "");

        const mappedProducts: PurchaseOrderProduct[] =
          order?.products?.map((item: any) => {
            const productIdValue =
              typeof item.productId === "string"
                ? item.productId
                : item.productId?._id || "";

            return {
              productId: productIdValue,
              name: item.productId?.name || item.name || "",
              unitPrice: Number(item.unitPrice || 0),
              taxPercent: Number(item.taxPercent || TAX_PERCENT),
              quantity: Number(item.quantity || 0),
              totalPrice: Number(item.totalPrice || 0),
            };
          }) || [];

        setCart(mappedProducts);

        if (loadedStatus !== "draft") {
          toast.error("Only draft purchases can be edited");
          navigate("/app/purchase/list");
        }
      } catch (error) {
        console.error("Error fetching order details:", error);
        toast.error("Failed to fetch order details");
      } finally {
        setIsLoadingDetails(false);
      }
    };

    if (purchaseId) {
      loadPurchaseDetails();
    }
  }, [purchaseId, navigate]);

  const handleClose = useCallback(() => {
    dispatch(purchaseActions.resetCreatePurchase());
    dispatch(purchaseActions.resetEditPurchase());
    navigate("/app/purchase/list");
  }, [dispatch, navigate]);

  useEffect(() => {
    if (create.data) {
      toast.success("Purchase order created successfully!");
      handleClose();
    }
  }, [create.data, handleClose]);

  useEffect(() => {
    if (edit.data) {
      toast.success("Purchase order updated successfully!");
      handleClose();
    } else if (edit.error) {
      toast.error(`Error: ${edit.error}`);
    }
  }, [edit, handleClose]);

  // ------------------ Cart Logic ------------------
  const handleAddProduct = (productId: string) => {
    const product = products.find((p) => p._id === productId);
    if (!product) return;

    if (cart.some((c) => c.productId === productId)) {
      toast.warning("Product already added");
      return;
    }

    setCart([
      ...cart,
      {
        productId: product._id || "",
        name: product?.name || "",
        unitPrice: Number(product.sellingPrice || 0),
        taxPercent: TAX_PERCENT,
        quantity: 1,
        totalPrice: Number(product.sellingPrice || 0),
      },
    ]);
  };

  const handleUpdateItem = (
    index: number,
    field: keyof PurchaseOrderProduct,
    value: any
  ) => {
    const updated = [...cart];
    // @ts-ignore
    updated[index][field] = value;

    const item = updated[index];
    const qty = parseFloat(String(item.quantity)) || 0;
    const total = qty * (item.unitPrice || 0);
    updated[index].totalPrice = Number(total.toFixed(2));
    setCart(updated);
  };

  const handleRemoveItem = (index: number) => {
    setCart(cart.filter((_, i) => i !== index));
  };

  // ------------------ Totals ------------------
  const discountPercentValue = parseFloat(discountPercentage) || 0;
  const subTotal = cart.reduce((sum, item) => sum + item.totalPrice, 0);
  const taxValue = (subTotal * TAX_PERCENT) / 100;
  const discountAmount = (subTotal * discountPercentValue) / 100;
  const grandTotal = subTotal + taxValue - discountAmount;

  // ------------------ Submit ------------------
  const handleSubmit = (status: "completed" | "draft" = "completed") => {
    if (!supplier) {
      toast.error("Please select a supplier");
      return;
    }
    if (cart.length === 0) {
      toast.error("Please add at least one product");
      return;
    }

    const payload = {
      supplierId: supplier?._id || "",
      products: cart,
      remarks,
      createdBy: user?._id || "",
      paymentStatus: "paid" as const,
      orderStatus: status,
    };

    if (purchaseId) {
      dispatch(
        purchaseActions.editPurchaseRequest({
          id: purchaseId,
          data: payload,
        })
      );
      return;
    }

    dispatch(purchaseActions.createPurchaseRequest(payload));
  };

  const pageTitle = purchaseId ? "Edit Purchase" : "Create Purchase";

  return (
    <DashboardLayout
      meta={{
        title: purchaseId
          ? "Edit Purchase | Invyntra"
          : "Create Purchase | Invyntra",
        description: "Create purchase orders.",
      }}
    >
      <div className="space-y-4">
        <PageHeader
          title={pageTitle}
          showBack={!!purchaseId}
          breadcrumbData={[
            { label: "Home", path: "/app/dashboard" },
            { label: "Purchases", path: "/app/purchase/list" },
            { label: pageTitle, path: "" },
          ]}
        />

        <Card>
          <div className="space-y-4">
            {/* ---------------- SUPPLIER INFO ---------------- */}
            <div className="border rounded-lg p-4 bg-gray-50">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">
                Supplier Information
              </h3>

              <Dropdown
                label="Select Supplier"
                options={(suppliers as any)?.map((s: Supplier) => ({
                  label: `${s.name} (${s.supplierId})`,
                  value: s?._id,
                }))}
                onChange={(val) => {
                  setSupplierId(val);
                  const selected = suppliers?.filter(
                    (supplier) => supplier?._id === val
                  )?.[0];
                  setSupplier(selected);
                }}
                value={supplierId}
              />

              {supplier && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3 text-sm">
                  <p>
                    <strong>Name:</strong> {supplier.name}
                  </p>
                  <p>
                    <strong>GST:</strong> {supplier.gstNumber || "N/A"}
                  </p>
                  <p>
                    <strong>Email:</strong> {supplier.email || "—"}
                  </p>
                  <p>
                    <strong>Phone:</strong> {supplier.phone || "—"}
                  </p>
                </div>
              )}
            </div>

            {/* ---------------- PRODUCTS ---------------- */}
            <div className="border rounded-lg p-4 bg-gray-50">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">
                Products
              </h3>

              <Dropdown
                label="Add Product"
                options={(products as any)?.map((p: Product) => ({
                  label: `${p.name} (${p.sku || p.productId})`,
                  value: p._id,
                }))}
                onChange={(val) => handleAddProduct(val)}
                value=""
              />

              {cart.length > 0 && (
                <div className="mt-4">
                  <TableV2
                    columns={[
                      { key: "name", header: "Product" },
                      {
                        key: "quantity",
                        header: "Qty",
                        accessor: ({ row, rowIndex }) => (
                          <input
                            type="text"
                            className="w-16 border rounded-md px-2 py-1 text-sm text-center"
                            value={row.quantity}
                            onChange={(e) =>
                              handleUpdateItem(
                                rowIndex,
                                "quantity",
                                e.target.value
                              )
                            }
                          />
                        ),
                      },
                      {
                        key: "unitPrice",
                        header: "Price",
                        accessor: ({ row, rowIndex }) => (
                          <input
                            type="text"
                            className="w-16 border rounded-md px-2 py-1 text-sm text-center"
                            value={row.unitPrice}
                            onChange={(e) =>
                              handleUpdateItem(
                                rowIndex,
                                "unitPrice",
                                e.target.value
                              )
                            }
                          />
                        ),
                      },
                      {
                        key: "totalPrice",
                        header: "Total",
                        align: "right",
                        accessor: ({ row }) =>
                          currencyFormatter(row.totalPrice, "IN", 2),
                      },
                      {
                        key: "action",
                        header: "",
                        accessor: ({ rowIndex }) => (
                          <button
                            onClick={() => handleRemoveItem(rowIndex)}
                            className="hover:bg-red-100 text-red-600 px-2 py-2 rounded-md"
                          >
                            <Trash size={16} />
                          </button>
                        ),
                      },
                    ]}
                    data={cart}
                  />
                </div>
              )}
            </div>

            {/* ---------------- PURCHASE SUMMARY ---------------- */}
            <div className="border rounded-lg p-4 bg-gray-50 space-y-3">
              <h3 className="text-sm font-semibold text-gray-700">
                Purchase Summary
              </h3>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <Input
                  label={`Tax (${TAX_PERCENT}%)`}
                  value={currencyFormatter(taxValue)}
                  readOnly
                />
                <Input
                  label="Discount (%)"
                  type="number"
                  value={discountPercentage}
                  onChange={(e) => setDiscountPercentage(e.target.value)}
                  placeholder="Enter discount %"
                />
                <Dropdown
                  label="Payment Status"
                  value={paymentStatus}
                  options={[{ label: "Paid", value: "paid" }]}
                  onChange={(val) => setPaymentStatus("paid")}
                  disabled
                />
              </div>

              <Textarea
                label="Remarks"
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                placeholder="Additional notes..."
              />

              <div className="flex justify-end border-t pt-3 mt-2">
                <div className="text-right space-y-1">
                  <div className="text-sm text-gray-600">
                    Subtotal: {currencyFormatter(subTotal)}
                  </div>
                  <div className="text-sm text-gray-600">
                    Tax ({TAX_PERCENT}%): {currencyFormatter(taxValue)}
                  </div>
                  <div className="text-sm text-gray-600">
                    Discount ({discountPercentValue}%):{" "}
                    {currencyFormatter(discountAmount)}
                  </div>
                  <div className="font-semibold text-gray-800 text-lg">
                    Grand Total: {currencyFormatter(grandTotal)}
                  </div>
                </div>
              </div>
            </div>

            {/* ---------------- ACTION BUTTONS ---------------- */}
            <div className="flex justify-end space-x-3 pb-2">
              <Button onClick={handleClose} variant="outline">
                Cancel
              </Button>
              <Button
                onClick={() => handleSubmit("draft")}
                variant="outline"
                isLoading={create.loading || edit.loading || isLoadingDetails}
                disabled={isLoadingDetails}
              >
                Save as Draft
              </Button>
              <Button
                onClick={() => handleSubmit("completed")}
                className="bg-indigo-600 text-white"
                isLoading={create.loading || edit.loading || isLoadingDetails}
                disabled={isLoadingDetails}
              >
                {purchaseId ? "Update Purchase" : "Save Purchase"}
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default PurchaseForm;
